import { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition, Tab } from '@headlessui/react';
import { XIcon, UserGroupIcon, LinkIcon } from '@heroicons/react/outline';

export default function GroupModal({ isOpen, onClose, onSelect, group = null, isEdit = false }) {
  // تحديد التاب الافتراضي بناءً على نوع القروب (إذا كان في وضع التعديل)
  const getInitialTab = () => {
    if (group && group.groupType) {
      return group.groupType === 'telegram' ? 0 : 1;
    }
    return 0;
  };
  
  const [selectedTab, setSelectedTab] = useState(getInitialTab());
  const [title, setTitle] = useState(group ? group.title : '');
  const [description, setDescription] = useState(group ? group.description || '' : '');
  const [link, setLink] = useState(group ? group.link : '');
  
  // تحديث الحقول عند تغيير القروب
  useEffect(() => {
    if (group) {
      setTitle(group.title || '');
      setDescription(group.description || '');
      setLink(group.link || '');
      setSelectedTab(group.groupType === 'telegram' ? 0 : 1);
    } else {
      // إعادة تعيين الحقول إذا كان المودال للإضافة
      setTitle('');
      setDescription('');
      setLink('');
      setSelectedTab(0);
    }
  }, [group, isOpen]);

  const handleSubmit = (type) => {
    // معالجة تقديم نموذج المجموعة
    
    if (!title || !link) {
      // التحقق من وجود العنوان والرابط
      return;
    }
    
    const groupData = {
      type: 'group',
      groupType: type === 'telegram' ? 'telegram' : 'whatsapp',
      title,
      description,
      link
    };
    
    // إضافة معرف القروب إذا كنا في وضع التعديل
    if (isEdit && group && group.id) {
      groupData.id = group.id;
    }
    
    // تجهيز بيانات المجموعة للإرسال
    
    try {
      // استخدام onSelect لإرسال بيانات القروب
      if (typeof onSelect === 'function') {
        // استدعاء دالة onSelect
        onSelect(groupData);
      } else {
        // تنبيه: onSelect ليست دالة
      }
      
      // تنظيف الحالة
      setTitle('');
      setDescription('');
      setLink('');
      onClose();
    } catch (error) {
      // معالجة الخطأ في handleSubmit
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 p-5 shadow-xl transition-all">
                <div className="flex justify-between items-center mb-3">
                  <Dialog.Title className="text-lg font-medium text-gray-900 dark:text-white">
                    {isEdit ? 'تعديل قروب' : 'إضافة قروب'}
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                  >
                    <XIcon className="w-5 h-5" />
                  </button>
                </div>

                <Tab.Group selectedIndex={selectedTab} onChange={setSelectedTab}>
                  <Tab.List className="flex space-x-2 rounded-lg bg-gray-100 dark:bg-gray-700/50 p-1 mb-3 rtl:space-x-reverse">
                    <Tab
                      className={({ selected }) =>
                        `w-full rounded-lg py-2.5 text-sm font-medium leading-5 flex items-center justify-center gap-2
                        ${selected
                          ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow'
                          : 'text-gray-600 dark:text-gray-400 hover:bg-white/[0.12] dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white'
                        }`
                      }
                    >
                      <UserGroupIcon className="w-5 h-5" />
                      قروب تيليجرام
                    </Tab>
                    <Tab
                      className={({ selected }) =>
                        `w-full rounded-lg py-2.5 text-sm font-medium leading-5 flex items-center justify-center gap-2
                        ${selected
                          ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow'
                          : 'text-gray-600 dark:text-gray-400 hover:bg-white/[0.12] dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white'
                        }`
                      }
                    >
                      <UserGroupIcon className="w-5 h-5" />
                      قروب واتساب
                    </Tab>
                  </Tab.List>

                  <Tab.Panels>
                    <Tab.Panel>
                      <div className="space-y-2.5">
                        {/* عنوان القروب */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-0.5">
                            العنوان
                          </label>
                          <input
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            className="w-full px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 focus:border-black dark:focus:border-white focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            placeholder="أدخل عنوان القروب"
                          />
                        </div>

                        {/* وصف القروب */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-0.5">
                            الوصف (اختياري)
                          </label>
                          <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows="2"
                            className="w-full px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 focus:border-black dark:focus:border-white focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            placeholder="أدخل وصفاً مختصراً"
                          />
                        </div>

                        {/* رابط القروب */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-0.5">
                            رابط القروب
                          </label>
                          <input
                            type="url"
                            value={link}
                            onChange={(e) => setLink(e.target.value)}
                            className="w-full px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 focus:border-black dark:focus:border-white focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            placeholder="https://t.me/..."
                            dir="ltr"
                          />
                        </div>

                        {/* زر الإضافة */}
                        <div className="mt-4 flex justify-center">
                          <button
                            onClick={() => handleSubmit(selectedTab === 0 ? 'telegram' : 'whatsapp')}
                            disabled={!title || !link}
                            className="px-6 py-2.5 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed w-2/3 mx-auto transition-colors"
                          >
                            إضافة {selectedTab === 0 ? 'قروب تيليجرام' : 'قروب واتساب'}
                          </button>
                        </div>
                      </div>
                    </Tab.Panel>

                    <Tab.Panel>
                      <div className="space-y-2.5">
                        {/* عنوان القروب */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-0.5">
                            العنوان
                          </label>
                          <input
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            className="w-full px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 focus:border-black dark:focus:border-white focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            placeholder="أدخل عنوان القروب"
                          />
                        </div>

                        {/* وصف القروب */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-0.5">
                            الوصف (اختياري)
                          </label>
                          <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows="2"
                            className="w-full px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 focus:border-black dark:focus:border-white focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            placeholder="أدخل وصفاً مختصراً"
                          />
                        </div>

                        {/* رابط القروب */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-0.5">
                            رابط القروب
                          </label>
                          <input
                            type="url"
                            value={link}
                            onChange={(e) => setLink(e.target.value)}
                            className="w-full px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 focus:border-black dark:focus:border-white focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            placeholder="https://chat.whatsapp.com/..."
                            dir="ltr"
                          />
                        </div>

                        {/* زر الإضافة */}
                        <div className="mt-4 flex justify-center">
                          <button
                            onClick={() => handleSubmit(selectedTab === 0 ? 'telegram' : 'whatsapp')}
                            disabled={!title || !link}
                            className="px-6 py-2.5 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed w-2/3 mx-auto transition-colors"
                          >
                            إضافة {selectedTab === 0 ? 'قروب تيليجرام' : 'قروب واتساب'}
                          </button>
                        </div>
                      </div>
                    </Tab.Panel>
                  </Tab.Panels>
                </Tab.Group>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
